import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Box,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import './ReturnExperienceChart.css';

const DEFAULT_MODEL = 'Active';
const DEFAULT_METRIC_KEY = 'return_experience';
const PERCENTILE_Z_SCORES = {
  p05: -1.6448536269514722,
  p25: -0.6744897501960817,
  p50: 0,
  p75: 0.6744897501960817,
  p95: 1.6448536269514722,
};
const ASSUMPTION_TOLERANCE = 1e-12;
const Y_AXIS_STEPS = [0.05, 0.1, 0.2];

const createAssumptionState = () => ({
  Absolute: { annualReturn: '', annualRisk: '' },
  Active: { annualReturn: '', annualRisk: '' },
});

const formatPercent = (value, digits = 2) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'N/A';
  }
  return `${(numericValue * 100).toFixed(digits)}%`;
};

const formatInputPercent = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '';
  }
  return (numericValue * 100).toFixed(2);
};

const parsePercentInput = (value) => {
  const normalizedValue = String(value ?? '').trim();
  if (!normalizedValue) {
    return null;
  }

  const numericValue = Number(normalizedValue);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return numericValue / 100;
};

const normalizeSeries = (series) => (
  (Array.isArray(series) ? series : [])
    .map((row) => {
      const years = Number(row?.years);
      const p05 = Number(row?.p05);
      const p25 = Number(row?.p25);
      const p50 = Number(row?.p50);
      const p75 = Number(row?.p75);
      const p95 = Number(row?.p95);

      if (![years, p05, p25, p50, p75, p95].every(Number.isFinite)) {
        return null;
      }

      return {
        years,
        p05,
        p25,
        p50,
        p75,
        p95,
      };
    })
    .filter(Boolean)
);

const buildExpectedSeries = ({ annualReturn, annualRisk, periodsPerYear, horizonYears }) => {
  const resolvedPeriodsPerYear = Math.max(1, Math.round(Number(periodsPerYear) || 12));
  const resolvedHorizonYears = Math.max(1, Number(horizonYears) || 5);
  const horizonObservations = Math.max(1, Math.round(resolvedPeriodsPerYear * resolvedHorizonYears));
  const monthlyMean = Number(annualReturn) / resolvedPeriodsPerYear;
  const monthlyRisk = Math.max(0, Number(annualRisk) || 0) / Math.sqrt(resolvedPeriodsPerYear);

  return Array.from({ length: horizonObservations + 1 }, (_, step) => {
    const years = step / resolvedPeriodsPerYear;
    const cumulativeMean = monthlyMean * step;
    const cumulativeRisk = monthlyRisk * Math.sqrt(step);
    const p05 = step === 0 ? 0 : cumulativeMean + (PERCENTILE_Z_SCORES.p05 * cumulativeRisk);
    const p25 = step === 0 ? 0 : cumulativeMean + (PERCENTILE_Z_SCORES.p25 * cumulativeRisk);
    const p50 = step === 0 ? 0 : cumulativeMean + (PERCENTILE_Z_SCORES.p50 * cumulativeRisk);
    const p75 = step === 0 ? 0 : cumulativeMean + (PERCENTILE_Z_SCORES.p75 * cumulativeRisk);
    const p95 = step === 0 ? 0 : cumulativeMean + (PERCENTILE_Z_SCORES.p95 * cumulativeRisk);

    return {
      years,
      p05,
      p25,
      p50,
      p75,
      p95,
    };
  });
};

const roundToStep = (value, step, direction) => {
  const roundedValue = direction === 'down'
    ? Math.floor(value / step) * step
    : Math.ceil(value / step) * step;
  return Number(roundedValue.toFixed(10));
};

const buildPercentAxis = (...seriesCollections) => {
  const values = seriesCollections
    .flat()
    .flatMap((row) => [row?.p05, row?.p25, row?.p50, row?.p75, row?.p95])
    .filter((value) => Number.isFinite(value));

  if (!values.length) {
    return {
      domain: [-0.1, 0.1],
      ticks: [-0.1, -0.05, 0, 0.05, 0.1],
    };
  }

  const minValue = Math.min(0, ...values);
  const maxValue = Math.max(0, ...values);
  const step = Y_AXIS_STEPS.find((candidateStep) => {
    const lowerBound = roundToStep(minValue, candidateStep, 'down');
    const upperBound = roundToStep(maxValue, candidateStep, 'up');
    const tickCount = Math.round((upperBound - lowerBound) / candidateStep) + 1;
    return tickCount <= 9;
  }) || Y_AXIS_STEPS[Y_AXIS_STEPS.length - 1];

  const lowerBound = roundToStep(minValue, step, 'down');
  const upperBound = roundToStep(maxValue, step, 'up');
  const tickCount = Math.round((upperBound - lowerBound) / step);
  const ticks = Array.from({ length: tickCount + 1 }, (_, index) =>
    Number((lowerBound + (index * step)).toFixed(10)),
  );

  return {
    domain: [ticks[0], ticks[ticks.length - 1]],
    ticks,
  };
};

const buildYearTicks = (horizonYears) => {
  const roundedYears = Math.max(1, Math.round(Number(horizonYears) || 5));
  return Array.from({ length: roundedYears }, (_, index) => index + 1);
};

const ExperienceTooltip = ({ active, payload }) => {
  if (!active || !Array.isArray(payload) || !payload.length) {
    return null;
  }

  const row = payload[0]?.payload;
  if (!row) {
    return null;
  }

  return (
    <div className="return-experience__tooltip">
      <div className="return-experience__tooltip-title">{`${row.years.toFixed(1)} years`}</div>
      <p className="return-experience__tooltip-copy">95th percentile: {formatPercent(row.p95)}</p>
      <p className="return-experience__tooltip-copy">75th percentile: {formatPercent(row.p75)}</p>
      <p className="return-experience__tooltip-copy">Median: {formatPercent(row.p50)}</p>
      <p className="return-experience__tooltip-copy">25th percentile: {formatPercent(row.p25)}</p>
      <p className="return-experience__tooltip-copy">5th percentile: {formatPercent(row.p05)}</p>
    </div>
  );
};

const ExperiencePanel = ({
  data,
  title,
  subtitle,
  emptyMessage,
  yDomain,
  yTicks,
  xTicks,
  xDomain,
  showLegend = false,
}) => (
  <div className="return-experience__panel">
    <div className="return-experience__panel-header">
      <Typography variant="h6" className="return-experience__panel-title">
        {title}
      </Typography>
      <Typography variant="body2" className="return-experience__panel-subtitle">
        {subtitle}
      </Typography>
    </div>

    {data.length ? (
      <div className="return-experience__chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 18, left: 8, bottom: 18 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#dbe2ea" />
            <ReferenceLine y={0} stroke="#111827" strokeOpacity={0.7} />
            <XAxis
              type="number"
              dataKey="years"
              domain={xDomain}
              ticks={xTicks}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => `${value}`}
              label={{
                value: 'Years',
                position: 'insideBottom',
                offset: -8,
                style: {
                  fill: '#475467',
                  fontSize: 12,
                },
              }}
            />
            <YAxis
              width={76}
              axisLine={false}
              tickLine={false}
              domain={yDomain}
              ticks={yTicks}
              allowDecimals
              tickFormatter={(value) => formatPercent(value, 0)}
            />
            <Tooltip
              content={<ExperienceTooltip />}
              cursor={{ stroke: '#94a3b8', strokeDasharray: '4 4' }}
            />
            <Line
              type="monotone"
              dataKey="p05"
              stroke="#7b8794"
              strokeWidth={2.5}
              strokeDasharray="6 4"
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="p95"
              stroke="#7b8794"
              strokeWidth={2.5}
              strokeDasharray="6 4"
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="p25"
              stroke="#9fb7cd"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="p75"
              stroke="#9fb7cd"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="p50"
              stroke="#0b6b95"
              strokeWidth={3}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    ) : (
      <Alert severity="info" sx={{ mt: 1 }}>
        {emptyMessage}
      </Alert>
    )}

    {showLegend && (
      <div className="return-experience__legend">
        <div className="return-experience__legend-item">
          <span className="return-experience__legend-swatch return-experience__legend-swatch--quartile" />
          <span>25th/75th percentile</span>
        </div>
        <div className="return-experience__legend-item">
          <span className="return-experience__legend-swatch return-experience__legend-swatch--dashed" />
          <span>5th-95th percentile</span>
        </div>
        <div className="return-experience__legend-item">
          <span className="return-experience__legend-swatch return-experience__legend-swatch--median" />
          <span>Median</span>
        </div>
      </div>
    )}
  </div>
);

const ReturnExperienceChart = ({
  data,
  metricKey = DEFAULT_METRIC_KEY,
  selectedType: controlledSelectedType,
  onTypeChange,
  showTypeToggle = true,
}) => {
  const [uncontrolledSelectedType, setUncontrolledSelectedType] = useState(DEFAULT_MODEL);
  const [assumptions, setAssumptions] = useState(createAssumptionState);
  const selectedType = controlledSelectedType || uncontrolledSelectedType;

  const absolutePayload = data?.Absolute?.[0]?.[metricKey] || null;
  const activePayload = data?.Active?.[0]?.[metricKey] || null;
  const payload = data?.[selectedType]?.[0]?.[metricKey] || null;

  useEffect(() => {
    setAssumptions((previous) => {
      const next = { ...previous };

      [
        ['Absolute', absolutePayload],
        ['Active', activePayload],
      ].forEach(([modelLabel, modelPayload]) => {
        if (!modelPayload) {
          return;
        }

        next[modelLabel] = {
          annualReturn:
            next[modelLabel]?.annualReturn || formatInputPercent(modelPayload?.default_annual_return),
          annualRisk:
            next[modelLabel]?.annualRisk || formatInputPercent(modelPayload?.default_annual_risk),
        };
      });

      return next;
    });
  }, [absolutePayload, activePayload]);

  const currentAssumptions = assumptions[selectedType] || createAssumptionState()[selectedType];
  const parsedAnnualReturn = parsePercentInput(currentAssumptions.annualReturn);
  const parsedAnnualRisk = parsePercentInput(currentAssumptions.annualRisk);

  const annualReturnInputInvalid =
    String(currentAssumptions.annualReturn ?? '').trim() !== '' && parsedAnnualReturn == null;
  const annualRiskInputInvalid =
    String(currentAssumptions.annualRisk ?? '').trim() !== '' &&
    (parsedAnnualRisk == null || parsedAnnualRisk < 0);

  const defaultAnnualReturn = Number(payload?.default_annual_return);
  const defaultAnnualRisk = Number(payload?.default_annual_risk);
  const effectiveAnnualReturn = annualReturnInputInvalid || parsedAnnualReturn == null
    ? defaultAnnualReturn
    : parsedAnnualReturn;
  const effectiveAnnualRisk = annualRiskInputInvalid || parsedAnnualRisk == null
    ? defaultAnnualRisk
    : parsedAnnualRisk;

  const historicalSeries = useMemo(
    () => normalizeSeries(payload?.historical?.series),
    [payload],
  );

  const usesDefaultExpectedSeries =
    Math.abs(effectiveAnnualReturn - defaultAnnualReturn) <= ASSUMPTION_TOLERANCE &&
    Math.abs(effectiveAnnualRisk - defaultAnnualRisk) <= ASSUMPTION_TOLERANCE;

  const expectedSeries = useMemo(() => {
    if (!payload) {
      return [];
    }

    if (usesDefaultExpectedSeries) {
      const backendSeries = normalizeSeries(payload?.expected?.series);
      if (backendSeries.length) {
        return backendSeries;
      }
    }

    return buildExpectedSeries({
      annualReturn: effectiveAnnualReturn,
      annualRisk: effectiveAnnualRisk,
      periodsPerYear: payload?.periods_per_year,
      horizonYears: payload?.horizon_years,
    });
  }, [effectiveAnnualReturn, effectiveAnnualRisk, payload, usesDefaultExpectedSeries]);

  const yAxis = useMemo(
    () => buildPercentAxis(historicalSeries, expectedSeries),
    [expectedSeries, historicalSeries],
  );

  const xTicks = useMemo(
    () => buildYearTicks(payload?.horizon_years),
    [payload?.horizon_years],
  );
  const xDomain = useMemo(
    () => [0, Math.max(...xTicks)],
    [xTicks],
  );

  const handleTypeChange = (event, nextType) => {
    void event;
    if (nextType !== null) {
      if (onTypeChange) {
        onTypeChange(nextType);
      } else {
        setUncontrolledSelectedType(nextType);
      }
    }
  };

  const handleAssumptionChange = (field) => (event) => {
    const { value } = event.target;
    setAssumptions((previous) => ({
      ...previous,
      [selectedType]: {
        ...previous[selectedType],
        [field]: value,
      },
    }));
  };

  const handleResetAssumptions = () => {
    if (!payload) {
      return;
    }

    setAssumptions((previous) => ({
      ...previous,
      [selectedType]: {
        annualReturn: formatInputPercent(payload?.default_annual_return),
        annualRisk: formatInputPercent(payload?.default_annual_risk),
      },
    }));
  };

  if (!payload) {
    return (
      <Alert severity="info">
        Return experience charts will appear here once this analysis stage completes.
      </Alert>
    );
  }

  const riskLabel = payload?.risk_label || 'Risk';
  const historicalPathCount = Number(payload?.historical?.path_count) || 0;
  const firstStartDate = payload?.historical?.first_start_date || '';
  const lastStartDate = payload?.historical?.last_start_date || '';
  const horizonYears = Number(payload?.horizon_years) || 5;
  const startDateText =
    historicalPathCount > 0 && firstStartDate && lastStartDate
      ? `${historicalPathCount} forward paths from ${firstStartDate} to ${lastStartDate}.`
      : `A full ${horizonYears}-year forward history is not available for this series yet.`;

  return (
    <div className="return-experience">
      <div className="return-experience__controls">
        {showTypeToggle && (
          <ToggleButtonGroup
            value={selectedType}
            exclusive
            onChange={handleTypeChange}
            aria-label="Return experience type"
            size="small"
          >
            <ToggleButton value="Absolute" aria-label="Absolute">
              Absolute
            </ToggleButton>
            <ToggleButton value="Active" aria-label="Active">
              Active
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        {!showTypeToggle && <Box />}

        <div className="return-experience__assumptions">
          <TextField
            size="small"
            type="number"
            label="Expected Annual Return"
            value={currentAssumptions.annualReturn}
            onChange={handleAssumptionChange('annualReturn')}
            error={annualReturnInputInvalid}
            helperText={annualReturnInputInvalid ? 'Enter a numeric percentage.' : ' '}
            inputProps={{ step: '0.25' }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
          <TextField
            size="small"
            type="number"
            label={`Expected Annual ${riskLabel}`}
            value={currentAssumptions.annualRisk}
            onChange={handleAssumptionChange('annualRisk')}
            error={annualRiskInputInvalid}
            helperText={annualRiskInputInvalid ? 'Enter a non-negative percentage.' : ' '}
            inputProps={{ min: '0', step: '0.25' }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
          <Button variant="outlined" size="small" onClick={handleResetAssumptions}>
            Reset
          </Button>
        </div>
      </div>

      <Typography variant="body2" className="return-experience__note">
        Expected paths use the annual return and {riskLabel.toLowerCase()} assumptions shown above.
      </Typography>

      <div className="return-experience__grid">
        <ExperiencePanel
          data={historicalSeries}
          title={`Historic ${payload?.return_label} Experience`}
          subtitle={startDateText}
          emptyMessage={`At least ${horizonYears} years of contiguous return history are required to build the historical percentile paths.`}
          yDomain={yAxis.domain}
          yTicks={yAxis.ticks}
          xTicks={xTicks}
          xDomain={xDomain}
        />

        <ExperiencePanel
          data={expectedSeries}
          title={`Expected ${payload?.return_label} Experience`}
          subtitle={`${formatPercent(effectiveAnnualReturn)} annual return, ${formatPercent(effectiveAnnualRisk)} ${riskLabel.toLowerCase()}.`}
          emptyMessage="Expected percentile paths are unavailable."
          yDomain={yAxis.domain}
          yTicks={yAxis.ticks}
          xTicks={xTicks}
          xDomain={xDomain}
          showLegend
        />
      </div>
    </div>
  );
};

export default ReturnExperienceChart;
